import { useState } from "react";
import { Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, errorText } from "@/lib/api";

export const FB_STATUS = ["NEW", "UNDER REVIEW", "PLANNED", "IMPLEMENTED", "CLOSED"];
const CATS = ["Workbook feedback", "Improvement suggestion", "Feature request", "Resource request"];
const PACK = { "quick-start": "Toolkit 1: Quick Start", "resource-centre": "Resource Centre interest", workbook: "Workbook", "toolkit-1": "Toolkit 2: Resource Pack", "toolkit-4": "Toolkit 4: Improvement Planner" };

const Row = ({ label, value }) => (value ? <div className="mt-2"><dt className="text-sm font-bold text-ink-soft">{label}</dt><dd className="whitespace-pre-wrap">{value}</dd></div> : null);
const Sel = ({ label, value, options, onChange, testid }) => (
  <label className="flex flex-col gap-1 text-xs font-bold">{label}
    <select value={value || ""} onChange={(e) => onChange(e.target.value)} className="field-input py-1.5 text-sm" data-testid={testid}>
      {options.map((o) => <option key={o} value={o}>{o || "None"}</option>)}
    </select>
  </label>
);

const Item = ({ fb, onPatch, onDelete }) => (
  <li className="rounded-2xl border-2 border-line bg-surface p-5" data-testid={`feedback-item-${fb.id}`}>
    <div className="flex flex-wrap items-start justify-between gap-3">
      <div>
        <p className="font-bold">{PACK[fb.pack] || fb.pack} · {fb.role || "Role not given"}{fb.school ? ` · ${fb.school}` : ""}</p>
        <p className="text-sm text-ink-soft">{new Date(fb.created_at).toLocaleString("en-GB")}{fb.email ? ` · ${fb.email}` : " · Anonymous"}</p>
      </div>
      <div className="flex flex-wrap items-end gap-2">
        <Sel label="Status" value={fb.status || "NEW"} options={FB_STATUS} onChange={(v) => onPatch(fb, { status: v })} testid={`feedback-status-${fb.id}`} />
        <Sel label="Category" value={fb.category || "Workbook feedback"} options={CATS} onChange={(v) => onPatch(fb, { category: v })} testid={`feedback-category-${fb.id}`} />
        <Sel label="Priority" value={fb.priority} options={["", "Low", "Medium", "High"]} onChange={(v) => onPatch(fb, { priority: v })} testid={`feedback-priority-${fb.id}`} />
        <button type="button" onClick={() => onDelete(fb)} className="btn btn-ghost btn-sm" aria-label="Delete this feedback" data-testid={`feedback-delete-${fb.id}`}><Trash2 size={14} aria-hidden /></button>
      </div>
    </div>
    <dl>
      <Row label="Useful (1 to 5)" value={fb.rating ? `${fb.rating} / 5` : ""} />
      <Row label="Most useful section" value={fb.most_useful} />
      <Row label="Least useful section" value={fb.least_useful} />
      <Row label="What could be improved" value={fb.improve} />
      <Row label="Resources or features requested" value={fb.resources_requested || fb.build_next} />
      <Row label="Funded place interest" value={fb.founding} />
    </dl>
  </li>
);

export const FeedbackQueue = ({ items, setItems }) => {
  const [filter, setFilter] = useState("NEW");
  const patch = async (fb, p) => {
    try {
      await api.patch(`/admin/feedback/${fb.id}`, p);
      setItems((xs) => xs.map((x) => (x.id === fb.id ? { ...x, ...p } : x)));
    } catch (err) {
      toast.error(errorText(err));
    }
  };
  const del = async (fb) => {
    if (!window.confirm("Delete this feedback permanently?")) return;
    try {
      await api.delete(`/feedback/${fb.id}`);
      setItems((xs) => xs.filter((x) => x.id !== fb.id));
    } catch (err) {
      toast.error(errorText(err));
    }
  };
  const shown = filter === "ALL" ? items : items.filter((x) => (x.status || "NEW") === filter);
  return (
    <div data-testid="admin-feedback-queue">
      <div role="group" aria-label="Filter by status" className="mb-5 flex flex-wrap gap-2">
        {["ALL", ...FB_STATUS].map((s) => (
          <button key={s} type="button" aria-pressed={filter === s} onClick={() => setFilter(s)} className={`chip ${filter === s ? "bg-brand text-brand-fg" : ""}`} data-testid={`feedback-filter-${s.replace(" ", "-").toLowerCase()}`}>
            {s} ({s === "ALL" ? items.length : items.filter((x) => (x.status || "NEW") === s).length})
          </button>
        ))}
      </div>
      {shown.length ? <ul className="space-y-3">{shown.map((fb) => <Item key={fb.id} fb={fb} onPatch={patch} onDelete={del} />)}</ul> : <p className="text-ink-soft" data-testid="admin-empty">Nothing here.</p>}
    </div>
  );
};
